import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest, ApiRequestError } from '../../api/client.js';
import type {
  AdminFacility,
  AdminRole,
  AdminTaskOrder,
  AdminUser,
  Organization,
} from '../../api/types.js';
import { Modal } from '../../components/Modal.js';
import {
  Alert,
  Button,
  Card,
  EmptyState,
  Field,
  PageHeader,
  Select,
  Spinner,
  TextInput,
  classes,
} from '../../components/ui.js';
import { formatDateTime } from '../../lib/format.js';

export function AdminUsersTab() {
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [editingAccessFor, setEditingAccessFor] = useState<string | null>(null);
  const [invitationLink, setInvitationLink] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<AdminUser | null>(null);
  const [deleteTyped, setDeleteTyped] = useState('');

  const users = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () =>
      apiRequest<{ users: AdminUser[] }>('/api/admin/users').then((result) => result.users),
  });
  const organizations = useQuery({
    queryKey: ['admin', 'organizations'],
    queryFn: () =>
      apiRequest<{ organizations: Organization[] }>('/api/admin/organizations').then(
        (result) => result.organizations,
      ),
  });
  const roles = useQuery({
    queryKey: ['admin', 'roles'],
    queryFn: () =>
      apiRequest<{ roles: AdminRole[] }>('/api/admin/roles').then((result) => result.roles),
  });
  const facilities = useQuery({
    queryKey: ['admin', 'facilities'],
    queryFn: () =>
      apiRequest<{ facilities: AdminFacility[] }>('/api/admin/facilities').then(
        (result) => result.facilities,
      ),
  });
  const taskOrders = useQuery({
    queryKey: ['admin', 'task-orders'],
    queryFn: () =>
      apiRequest<{ taskOrders: AdminTaskOrder[] }>('/api/admin/task-orders').then(
        (result) => result.taskOrders,
      ),
  });

  const [form, setForm] = useState({
    email: '',
    fullName: '',
    organizationId: '',
    title: '',
    phone: '',
    roleCode: '',
    facilityIds: [] as string[],
    taskOrderIds: [] as string[],
    allTaskOrders: false,
    notifyPurchaseRequisition: false,
  });

  const selectedOrg = organizations.data?.find(
    (organization) => organization.id === form.organizationId,
  );
  const eligibleRoles = (roles.data ?? []).filter(
    (role) => !role.orgType || !selectedOrg || role.orgType === selectedOrg.orgType,
  );

  const create = useMutation({
    mutationFn: () =>
      apiRequest<{ id: string; invitationPath: string; expiresInHours: number }>(
        '/api/admin/users',
        {
          method: 'POST',
          body: {
            email: form.email.trim(),
            fullName: form.fullName.trim(),
            organizationId: form.organizationId,
            title: form.title.trim() || undefined,
            phone: form.phone.trim() || undefined,
            roleCodes: [form.roleCode],
            facilityIds: form.facilityIds,
            taskOrderIds: form.taskOrderIds,
            allTaskOrders: form.allTaskOrders,
            notifyPurchaseRequisition: form.notifyPurchaseRequisition,
          },
        },
      ),
    onSuccess: (result) => {
      setInvitationLink(`${window.location.origin}${result.invitationPath}`);
      setCreating(false);
      setForm({
        email: '',
        fullName: '',
        organizationId: '',
        title: '',
        phone: '',
        roleCode: '',
        facilityIds: [],
        taskOrderIds: [],
        allTaskOrders: false,
        notifyPurchaseRequisition: false,
      });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
    onError: (caught: unknown) => {
      setError(
        caught instanceof ApiRequestError
          ? caught.message
          : 'The user could not be created. Please try again.',
      );
    },
  });

  const reinvite = useMutation({
    mutationFn: (userId: string) =>
      apiRequest<{ invitationPath: string }>(`/api/admin/users/${userId}/reinvite`, {
        method: 'POST',
      }),
    onSuccess: (result) => {
      setInvitationLink(`${window.location.origin}${result.invitationPath}`);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });

  const setActive = useMutation({
    mutationFn: ({ userId, isActive }: { userId: string; isActive: boolean }) =>
      apiRequest(`/api/admin/users/${userId}`, { method: 'PATCH', body: { isActive } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'users'] }),
    onError: (caught: unknown) => {
      setError(
        caught instanceof ApiRequestError ? caught.message : 'The change could not be saved.',
      );
    },
  });

  const remove = useMutation({
    mutationFn: (userId: string) => apiRequest(`/api/admin/users/${userId}`, { method: 'DELETE' }),
    onSuccess: () => {
      setDeleting(null);
      setDeleteTyped('');
      void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
    onError: (caught: unknown) => {
      setDeleting(null);
      setDeleteTyped('');
      setError(
        caught instanceof ApiRequestError ? caught.message : 'The account could not be deleted.',
      );
    },
  });

  const saveAccess = useMutation({
    mutationFn: (input: {
      userId: string;
      roleCodes: string[];
      taskOrderIds: string[];
      facilityIds: string[];
      allTaskOrders: boolean;
      notifyPurchaseRequisition: boolean;
    }) =>
      apiRequest(`/api/admin/users/${input.userId}`, {
        method: 'PATCH',
        body: {
          roleCodes: input.roleCodes,
          taskOrderIds: input.taskOrderIds,
          facilityIds: input.facilityIds,
          allTaskOrders: input.allTaskOrders,
          notifyPurchaseRequisition: input.notifyPurchaseRequisition,
        },
      }),
    onSuccess: () => {
      setEditingAccessFor(null);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
    onError: (caught: unknown) => {
      setError(
        caught instanceof ApiRequestError ? caught.message : 'The change could not be saved.',
      );
    },
  });

  const canSubmit =
    form.email.trim() !== '' &&
    form.fullName.trim().length >= 2 &&
    form.organizationId !== '' &&
    form.roleCode !== '';

  return (
    <>
      <PageHeader
        title="Users"
        subtitle="Accounts are invite-only. Nobody, including an administrator, ever sets another person's password."
        actions={
          <Button onClick={() => setCreating((previous) => !previous)}>
            {creating ? 'Cancel' : 'Invite user'}
          </Button>
        }
      />

      {error && (
        <Alert variant="error" className="mb-4">
          {error}
        </Alert>
      )}

      {invitationLink && (
        <Alert variant="success" title="Invitation created" className="mb-4">
          <p>
            Send this single-use link to the invitee. It is shown once and cannot be recovered
            later.
          </p>
          <code className="mt-2 block break-all rounded bg-white p-2 text-xs text-slate-800 ring-1 ring-slate-200">
            {invitationLink}
          </code>
          <Button
            variant="secondary"
            className="mt-2"
            onClick={() => void navigator.clipboard.writeText(invitationLink)}
          >
            Copy link
          </Button>
        </Alert>
      )}

      {creating && (
        <Card title="Invite a user" className="mb-4">
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Full name" htmlFor="fullName" required>
                <TextInput
                  id="fullName"
                  value={form.fullName}
                  onChange={(event) => setForm({ ...form, fullName: event.target.value })}
                />
              </Field>
              <Field label="Email" htmlFor="email" required>
                <TextInput
                  id="email"
                  type="email"
                  value={form.email}
                  onChange={(event) => setForm({ ...form, email: event.target.value })}
                />
              </Field>
              <Field label="Organization" htmlFor="organizationId" required>
                <Select
                  id="organizationId"
                  value={form.organizationId}
                  onChange={(event) =>
                    setForm({ ...form, organizationId: event.target.value, roleCode: '' })
                  }
                >
                  <option value="">Select an organization…</option>
                  {(organizations.data ?? []).map((organization) => (
                    <option key={organization.id} value={organization.id}>
                      {organization.name} ({organization.orgType === 'customer' ? 'USPS' : 'VGS'})
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Role" htmlFor="roleCode" required>
                <Select
                  id="roleCode"
                  value={form.roleCode}
                  onChange={(event) => setForm({ ...form, roleCode: event.target.value })}
                  disabled={form.organizationId === ''}
                >
                  <option value="">Select a role…</option>
                  {eligibleRoles.map((role) => (
                    <option key={role.code} value={role.code}>
                      {role.name}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Title" htmlFor="title">
                <TextInput
                  id="title"
                  value={form.title}
                  onChange={(event) => setForm({ ...form, title: event.target.value })}
                />
              </Field>
              <Field label="Phone" htmlFor="phone">
                <TextInput
                  id="phone"
                  value={form.phone}
                  onChange={(event) => setForm({ ...form, phone: event.target.value })}
                />
              </Field>
            </div>

            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={form.allTaskOrders}
                onChange={(event) => setForm({ ...form, allTaskOrders: event.target.checked })}
              />
              Access to all task orders, including ones created later
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={form.notifyPurchaseRequisition}
                onChange={(event) =>
                  setForm({ ...form, notifyPurchaseRequisition: event.target.checked })
                }
              />
              Email when USPS first approves a work order (Purchase Requisition needed)
            </label>

            <Field
              label="Task order access"
              htmlFor="taskOrderIds"
              hint="Grants every facility under the selected task orders."
            >
              <Select
                id="taskOrderIds"
                multiple
                size={Math.min(5, Math.max(3, taskOrders.data?.length ?? 3))}
                value={form.taskOrderIds}
                onChange={(event) =>
                  setForm({ ...form, taskOrderIds: selectedValues(event.target) })
                }
              >
                {(taskOrders.data ?? []).map((taskOrder) => (
                  <option key={taskOrder.id} value={taskOrder.id}>
                    {taskOrder.number} — {taskOrder.name}
                  </option>
                ))}
              </Select>
            </Field>

            <Field
              label="Additional facility access"
              htmlFor="facilityIds"
              hint="Only needed for facilities outside the task orders selected above."
            >
              <Select
                id="facilityIds"
                multiple
                size={Math.min(6, Math.max(3, facilities.data?.length ?? 3))}
                value={form.facilityIds}
                onChange={(event) =>
                  setForm({ ...form, facilityIds: selectedValues(event.target) })
                }
              >
                {(facilities.data ?? []).map((facility) => (
                  <option key={facility.id} value={facility.id}>
                    {facility.facilityCode} — {facility.name}
                  </option>
                ))}
              </Select>
            </Field>

            <div>
              <Button
                onClick={() => {
                  setError(null);
                  create.mutate();
                }}
                disabled={!canSubmit}
                loading={create.isPending}
              >
                Create user and invitation
              </Button>
            </div>
          </div>
        </Card>
      )}

      <Card bodyClassName="px-0 py-0 sm:px-0">
        {users.isLoading ? (
          <div className="p-4">
            <Spinner label="Loading users" />
          </div>
        ) : (users.data?.length ?? 0) === 0 ? (
          <EmptyState title="No users yet" description="Invite the first administrator." />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <caption className="sr-only">User accounts</caption>
              <thead className="bg-slate-50">
                <tr>
                  <th scope="col" className="table-header-cell">
                    Name
                  </th>
                  <th scope="col" className="table-header-cell">
                    Organization
                  </th>
                  <th scope="col" className="table-header-cell">
                    Roles
                  </th>
                  <th scope="col" className="table-header-cell">
                    Facilities
                  </th>
                  <th scope="col" className="table-header-cell">
                    Last sign-in
                  </th>
                  <th scope="col" className="table-header-cell">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {users.data?.map((user) => (
                  <tr key={user.id} className={user.isActive ? undefined : 'bg-slate-50'}>
                    <td className="table-cell-base">
                      <span className="font-medium text-slate-900">{user.fullName}</span>
                      <span className="block text-xs text-slate-600">{user.email}</span>
                      {!user.isActive && (
                        <span className="mt-0.5 inline-block rounded bg-slate-200 px-1.5 py-0.5 text-xs text-slate-700">
                          Deactivated
                        </span>
                      )}
                      {user.invitationPending && (
                        <span className="mt-0.5 inline-block rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-800">
                          Invitation pending
                        </span>
                      )}
                    </td>
                    <td className="table-cell-base">
                      {user.organizationName}
                      <span
                        className={classes(
                          'ml-1 rounded px-1.5 py-0.5 text-xs',
                          user.orgType === 'customer'
                            ? 'bg-sky-100 text-sky-800'
                            : 'bg-brand-100 text-brand-800',
                        )}
                      >
                        {user.orgType === 'customer' ? 'USPS' : 'VGS'}
                      </span>
                    </td>
                    <td className="table-cell-base">{user.roleCodes.join(', ')}</td>
                    <td className="table-cell-base">
                      {user.allTaskOrders
                        ? 'All task orders'
                        : user.facilityCodes.length === 0
                          ? '—'
                          : user.facilityCodes.join(', ')}
                    </td>
                    <td className="table-cell-base">
                      {user.lastLoginAt ? formatDateTime(user.lastLoginAt) : 'Never'}
                    </td>
                    <td className="table-cell-base">
                      <div className="flex gap-1">
                        <Button variant="ghost" onClick={() => reinvite.mutate(user.id)}>
                          Re-invite
                        </Button>
                        <Button
                          variant="ghost"
                          onClick={() =>
                            setEditingAccessFor((previous) =>
                              previous === user.id ? null : user.id,
                            )
                          }
                        >
                          {editingAccessFor === user.id ? 'Close' : 'Access'}
                        </Button>
                        <Button
                          variant="ghost"
                          onClick={() => {
                            setError(null);
                            setActive.mutate({ userId: user.id, isActive: !user.isActive });
                          }}
                        >
                          {user.isActive ? 'Deactivate' : 'Reactivate'}
                        </Button>
                        <Button
                          variant="ghost"
                          className="text-red-700"
                          onClick={() => {
                            setError(null);
                            setDeleteTyped('');
                            setDeleting(user);
                          }}
                        >
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {users.data?.map(
                  (user) =>
                    editingAccessFor === user.id && (
                      <tr key={`${user.id}-access`} className="bg-slate-50">
                        <td colSpan={6} className="px-4 py-4">
                          <AccessEditor
                            user={user}
                            roles={(roles.data ?? []).filter(
                              (role) => !role.orgType || role.orgType === user.orgType,
                            )}
                            taskOrders={taskOrders.data ?? []}
                            facilities={facilities.data ?? []}
                            saving={saveAccess.isPending}
                            onSave={(values) => {
                              setError(null);
                              saveAccess.mutate({ userId: user.id, ...values });
                            }}
                          />
                        </td>
                      </tr>
                    ),
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Modal
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        title="Delete this account permanently"
        description={
          deleting
            ? `${deleting.fullName} (${deleting.email}) loses access immediately; the login, sessions, roles, facility and task order access and any open invitation are erased and cannot be recovered. If the account appears in work order, proposal or employee document records, the name stays on those records for the retention period but the account itself is removed.`
            : undefined
        }
        footer={
          <>
            <Button variant="secondary" onClick={() => setDeleting(null)}>
              Back
            </Button>
            <Button
              variant="danger"
              loading={remove.isPending}
              disabled={!deleting || deleteTyped.trim().toLowerCase() !== deleting.email}
              onClick={() => deleting && remove.mutate(deleting.id)}
            >
              Delete account
            </Button>
          </>
        }
      >
        {deleting && (
          <Field label={`Type the email address (${deleting.email}) to confirm`}>
            <TextInput
              value={deleteTyped}
              onChange={(event) => setDeleteTyped(event.target.value)}
              autoComplete="off"
            />
          </Field>
        )}
      </Modal>
    </>
  );
}

/**
 * Role, task order scope and facility scope for an existing account. These are the
 * assignments the agreement puts in VGS's hands rather than the user's: an invitee
 * completes their own profile, but never their own authority.
 */
function AccessEditor({
  user,
  roles,
  taskOrders,
  facilities,
  saving,
  onSave,
}: {
  user: AdminUser;
  roles: AdminRole[];
  taskOrders: AdminTaskOrder[];
  facilities: AdminFacility[];
  saving: boolean;
  onSave: (values: {
    roleCodes: string[];
    taskOrderIds: string[];
    facilityIds: string[];
    allTaskOrders: boolean;
    notifyPurchaseRequisition: boolean;
  }) => void;
}) {
  const [roleCode, setRoleCode] = useState(user.roleCodes[0] ?? '');
  const [taskOrderIds, setTaskOrderIds] = useState(user.taskOrderIds);
  const [facilityIds, setFacilityIds] = useState(user.facilityIds);
  const [allTaskOrders, setAllTaskOrders] = useState(user.allTaskOrders);
  const [notifyPurchaseRequisition, setNotifyPurchaseRequisition] = useState(
    user.notifyPurchaseRequisition,
  );

  return (
    <div className="flex flex-col gap-4">
      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input
          type="checkbox"
          checked={allTaskOrders}
          onChange={(event) => setAllTaskOrders(event.target.checked)}
        />
        Access to all task orders, including ones created later
      </label>
      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input
          type="checkbox"
          checked={notifyPurchaseRequisition}
          onChange={(event) => setNotifyPurchaseRequisition(event.target.checked)}
        />
        Email when USPS first approves a work order (Purchase Requisition needed)
      </label>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Field label="Role" htmlFor={`role-${user.id}`} required>
          <Select
            id={`role-${user.id}`}
            value={roleCode}
            onChange={(event) => setRoleCode(event.target.value)}
          >
            {roles.map((role) => (
              <option key={role.code} value={role.code}>
                {role.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label="Task order access"
          htmlFor={`taskOrders-${user.id}`}
          hint="Grants every facility under the selected task orders."
        >
          <Select
            id={`taskOrders-${user.id}`}
            multiple
            size={Math.min(5, Math.max(3, taskOrders.length))}
            value={taskOrderIds}
            onChange={(event) => setTaskOrderIds(selectedValues(event.target))}
          >
            {taskOrders.map((taskOrder) => (
              <option key={taskOrder.id} value={taskOrder.id}>
                {taskOrder.number} — {taskOrder.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label="Additional facility access"
          htmlFor={`facilities-${user.id}`}
          hint="Only for facilities outside the task orders above."
        >
          <Select
            id={`facilities-${user.id}`}
            multiple
            size={Math.min(6, Math.max(3, facilities.length))}
            value={facilityIds}
            onChange={(event) => setFacilityIds(selectedValues(event.target))}
          >
            {facilities.map((facility) => (
              <option key={facility.id} value={facility.id}>
                {facility.facilityCode} — {facility.name}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      <p className="text-xs text-slate-600">
        Dollar approval limits are set per role and task order on the Approval authority tab, and
        are snapshotted onto each approval.
      </p>

      <div>
        <Button
          onClick={() =>
            onSave({
              roleCodes: [roleCode],
              taskOrderIds,
              facilityIds,
              allTaskOrders,
              notifyPurchaseRequisition,
            })
          }
          disabled={roleCode === ''}
          loading={saving}
        >
          Save access
        </Button>
      </div>
    </div>
  );
}

function selectedValues(element: HTMLSelectElement): string[] {
  return Array.from(element.selectedOptions).map((option) => option.value);
}
